import { useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

export default function AdminStaff() {
  const [items, setItems] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [form, setForm] = useState({
    phone: "",
    name: "",
    email: "",
    role: "manager",
  });

  const load = () => api.get("/admin/staff").then((r) => setItems(r.data.items || []));
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      await api.post("/admin/staff", form);
      toast.success("Staff added. They sign in via OTP.");
      setDrawer(false);
      setForm({ phone: "", name: "", email: "", role: "manager" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Deactivate ${u.name}?`)) return;
    await api.delete(`/admin/staff/${u.id}`);
    toast.success(`Deactivated`);
    load();
  };

  const changeRole = async (u, role) => {
    await api.patch(`/admin/staff/${u.id}`, { role });
    toast.success(`${u.name} → ${role}`);
    load();
  };

  return (
    <div className="space-y-5" data-testid="admin-staff">
      <div className="flex items-end justify-between">
        <div>
          <span className="label-overline text-jow-gold">Permissions</span>
          <h1 className="font-display text-3xl text-jow-navy mt-1">Staff</h1>
          <p className="text-xs text-jow-navy/55 mt-1">Super-admin-only module</p>
        </div>
        <button
          onClick={() => setDrawer(true)}
          className="btn-primary-jow text-[11px]"
          data-testid="new-staff-btn"
        >
          <Plus size={13} /> Add staff
        </button>
      </div>

      <table className="w-full bg-white border border-jow-navy/10 text-sm">
        <thead className="bg-jow-navy/5 uppercase tracking-widest text-[10px] text-jow-navy/70">
          <tr>
            <th className="text-left px-4 py-3">Name</th>
            <th className="text-left px-4 py-3">Phone</th>
            <th className="text-left px-4 py-3">Email</th>
            <th className="text-left px-4 py-3">Role</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((u) => (
            <tr key={u.id} className="border-t border-jow-navy/5" data-testid={`staff-${u.phone}`}>
              <td className="px-4 py-3 text-jow-navy">{u.name}</td>
              <td className="px-4 py-3 text-jow-navy/70">{u.phone}</td>
              <td className="px-4 py-3 text-jow-navy/70">{u.email || "—"}</td>
              <td className="px-4 py-3">
                <select
                  value={u.role}
                  onChange={(e) => changeRole(u, e.target.value)}
                  className="border border-jow-navy/15 px-2 py-1 text-xs bg-white"
                  data-testid={`staff-role-${u.phone}`}
                >
                  <option value="manager">manager</option>
                  <option value="super_admin">super_admin</option>
                </select>
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => remove(u)}
                  className="text-jow-navy/40"
                  data-testid={`staff-del-${u.phone}`}
                >
                  <Trash2 size={13} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {drawer && (
        <div
          className="fixed inset-0 z-40 bg-jow-navy/50 flex justify-end"
          onClick={() => setDrawer(false)}
          data-testid="staff-drawer"
        >
          <aside
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white h-full overflow-y-auto p-6 space-y-3"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl text-jow-navy">New staff</h2>
              <button onClick={() => setDrawer(false)} className="text-jow-navy/50">
                <X size={16} />
              </button>
            </div>
            <F label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="s-phone" />
            <F label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="s-name" />
            <F label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="s-email" />
            <div>
              <label className="label-overline block mb-1">Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full border border-jow-navy/15 px-3 py-2 text-sm bg-white"
                data-testid="s-role"
              >
                <option value="manager">Manager</option>
                <option value="super_admin">Super admin</option>
              </select>
            </div>
            <button onClick={save} className="btn-primary-jow w-full" data-testid="s-save">
              Add staff
            </button>
            <p className="text-[11px] text-jow-navy/55">
              They sign in via phone OTP — no password to share.
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}

function F({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label-overline block mb-1">{label}</label>
      <input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-jow-navy/15 px-3 py-2 text-sm bg-white"
        data-testid={testid}
      />
    </div>
  );
}
